import type { EpoRule, EpdRule, EpoRuleType } from '@/types/investor';

export const generateEpoDescription = (
  channel: 'wholesale' | 'correspondent',
  type: EpoRuleType,
  value: number
): string => {
  if (type === 'payments') {
    return `Borrower must have made ${value} payment${value !== 1 ? 's' : ''} to investor`;
  }

  if (type === 'days_since_funding' && channel === 'wholesale') {
    return `${value} day${value !== 1 ? 's' : ''} must have passed since loan funding`;
  }

  if (type === 'days_since_purchase' && channel === 'correspondent') {
    return `${value} day${value !== 1 ? 's' : ''} must have passed since loan purchase`;
  }

  if (type === 'calendar_month_end_after_purchase' && channel === 'correspondent') {
    return `EPO applies through the end of calendar month ${value} after the purchase month.`;
  }

  return 'Invalid EPO rule configuration';
};

export const createEpoRule = (
  channel: 'wholesale' | 'correspondent',
  type: EpoRuleType,
  value: number | undefined
): EpoRule => {
  return {
    type,
    value,
    description: value !== undefined ? generateEpoDescription(channel, type, value) : 'Configure EPO rule'
  };
};

export const getValidEpoTypes = (channel: 'wholesale' | 'correspondent'): Array<{
  value: EpoRuleType;
  label: string;
}> => {
  const baseTypes: Array<{ value: EpoRuleType; label: string }> = [
    { value: 'payments', label: 'Number of Payments Made' }
  ];

  if (channel === 'wholesale') {
    baseTypes.push({ value: 'days_since_funding', label: 'Days Since Loan Funding' });
  } else if (channel === 'correspondent') {
    baseTypes.push({ value: 'days_since_purchase', label: 'Days Since Loan Purchase' });
    baseTypes.push({ value: 'calendar_month_end_after_purchase', label: 'Through End of Calendar Month After Purchase' });
  }

  return baseTypes;
};

export const getDefaultEpoRule = (_channel: 'wholesale' | 'correspondent'): EpoRule => {
  return {
    type: 'payments',
    value: undefined,  // Start with blank field
    description: 'Configure EPO rule'
  };
};

// EPD Rule Functions
export const generateEpdDescription = (
  channel: 'wholesale' | 'correspondent',
  type: 'payments' | 'days_since_funding' | 'days_since_purchase',
  value: number
): string => {
  if (type === 'payments') {
    return `Borrower must make ${value} payment${value !== 1 ? 's' : ''} before default is allowed`;
  }

  if (type === 'days_since_funding' && channel === 'wholesale') {
    return `${value} day${value !== 1 ? 's' : ''} must pass since loan funding before default`;
  }

  if (type === 'days_since_purchase' && channel === 'correspondent') {
    return `${value} day${value !== 1 ? 's' : ''} must pass since loan purchase before default`;
  }

  return 'Invalid EPD rule configuration';
};

export const createEpdRule = (
  channel: 'wholesale' | 'correspondent',
  type: 'payments' | 'days_since_funding' | 'days_since_purchase',
  value: number | undefined
): EpdRule => {
  return {
    type,
    value,
    description: value !== undefined ? generateEpdDescription(channel, type, value) : 'Configure EPD rule'
  };
};

export const getValidEpdTypes = (channel: 'wholesale' | 'correspondent'): Array<{
  value: 'payments' | 'days_since_funding' | 'days_since_purchase';
  label: string;
}> => {
  const baseTypes: Array<{ value: 'payments' | 'days_since_funding' | 'days_since_purchase'; label: string }> = [
    { value: 'payments', label: 'Number of Payments Before Default' }
  ];

  if (channel === 'wholesale') {
    baseTypes.push({ value: 'days_since_funding', label: 'Days Since Loan Funding' });
  } else if (channel === 'correspondent') {
    baseTypes.push({ value: 'days_since_purchase', label: 'Days Since Loan Purchase' });
  }

  return baseTypes;
};

export const getDefaultEpdRule = (_channel: 'wholesale' | 'correspondent'): EpdRule => {
  return {
    type: 'payments',
    value: undefined,  // Start with blank field
    description: 'Configure EPD rule'
  };
};

// ---------------------------------------------------------------------------
// Shared rule validation — used by BOTH the slide-over and the Add-Investor
// wizard so the two editors cannot diverge. Kept here next to
// create*Rule/getValid*Types.
// ---------------------------------------------------------------------------

/**
 * The valid rule-type values depend on both channel and kind. Purchase-month
 * EPO must never become an allowed EPD type through the shared editors.
 */
export const getValidRuleTypes = (
  channel: 'wholesale' | 'correspondent',
  kind: 'epo' | 'epd'
): EpoRuleType[] =>
  (kind === 'epo' ? getValidEpoTypes(channel) : getValidEpdTypes(channel)).map((option) => option.value);

/** A rule is "configured" only when its value is a positive integer. */
export const isRuleConfigured = (
  rule?: { value?: number | string | null } | null
): boolean => {
  const raw = rule?.value;
  if (raw === undefined || raw === null || raw === '') {
    return false;
  }
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  return Number.isInteger(numeric) && numeric > 0;
};

/**
 * Coerce raw editor input to a storable rule value: a positive finite number, or
 * `undefined` for anything else (blank, 0, negative, NaN, the value:0 placeholder
 * convertFromDatabase produces). This guarantees a rule is NEVER built with value
 * 0 — blank stays value-less so normalize_investor_epo_rule drops it. Invalid-but-
 * positive entries (decimals, >12) are kept so getRuleValueError can flag them.
 */
export const normalizeRuleValue = (raw: unknown): number | undefined => {
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
};

/**
 * Validate an EPO/EPD rule for save. Returns null when the rule is blank
 * (unconfigured — allowed; the DB normalizer drops it and closes any active
 * version) or fully valid; otherwise a human-readable error string.
 *
 * Defensive against runtime JSON shapes (string/null/NaN), not just the EpoRule
 * TS type. Uses Number() + Number.isInteger — never parseInt, which would
 * silently accept "1.5" as 1.
 */
export const getRuleValueError = (
  label: string,
  rule: { type?: string | null; value?: number | string | null } | null | undefined,
  channel: 'wholesale' | 'correspondent',
  kind: 'epo' | 'epd'
): string | null => {
  if (!rule) {
    return null;
  }
  const raw = rule.value;
  // Blank — incl. the value:0 placeholder convertFromDatabase produces — is allowed (clears the rule).
  if (raw === undefined || raw === null || raw === '' || raw === 0 || raw === '0') {
    return null;
  }
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(numeric) || !Number.isInteger(numeric) || numeric <= 0) {
    return `${label} must be a whole number greater than 0`;
  }
  // A configured rule (value present, validated above) must carry a type the
  // channel and the DB normalizer accept — a missing type would be silently rejected.
  if (!rule.type || !(getValidRuleTypes(channel, kind) as string[]).includes(rule.type)) {
    return `${label} has an invalid type for the ${channel} channel`;
  }
  if (rule.type === 'payments' && numeric > 12) {
    return `${label} cannot exceed 12 payments`;
  }
  return null;
};
